import { AnyType, ILegoElem, IOption } from '@centra/centra-ui'
import { NG_API_CLIENT } from 'services/api'
import { IPropertyOption, ISubmitTaskPropertyValues } from './models'
import { PRODUCT_PROPERTY_GID, SUPPORT_TYPE_PROPERTY_GID, TELEGRAM_PROPERTY_OPTION_GID } from './consts'
import { CategoryTypeIds, IPropertyValue } from 'store/taskManager/model'
import { Roles } from '../../../utils'
import { IUserOption } from './UserDropdown'

export const getPropertyId = (data: any, propertyGid: string): number =>
  data.result?.data?.page?.entries?.find((p: any) => p.gid === propertyGid)?.id

export const getPropertyValue = (propertyValues: IPropertyValue[] | undefined, propertyId: number) =>
  propertyValues?.find(pv => pv.propertyId === propertyId)

export const getTelegramPropertyOption = (propertyOptions: IPropertyOption[]) =>
  propertyOptions.find(po => po.gid === TELEGRAM_PROPERTY_OPTION_GID)

export const getUniqueParticipantList = (participants: IUserOption[]): IUserOption[] => {
  if (!participants?.length) return []

  const ids = [] as number[]

  return participants.filter(item => {
    if (ids.includes(item.id)) return false
    ids.push(item.id)
    return true
  })
}

export const buildSubmitCancelButtons = (onClose: () => void): ILegoElem => {
  return {
    id: 'buttons',
    boxWrap: {
      marginTop: 32,
    },
    lego: {
      elems: [
        {
          id: 'submitBtn',
          button: {
            id: 'submitBtn',
            size: 'lg',
            view: 'primaryLight',
            type: 'submit',
          },
          content: 'Сохранить',
        },
        {
          id: 'cancelBtn',
          button: {
            id: 'cancelBtn',
            size: 'lg',
            view: 'secondaryLight',
            type: 'button',
            onClick: onClose,
          },
          content: 'Отменить',
        },
      ],
      boxWrap: {
        alignItems: 'center',
        justifyContent: 'spaceBetween',
        gap: 24,
      },
    },
  }
}

export const getFormElements = ({
  task,
  supportTypes,
  telegramSupportTypeId,
  onClose,
}: {
  task: AnyType
  supportTypes: IOption<number>[]
  telegramSupportTypeId?: number
  onClose: () => void
}) => {
  return [
    {
      id: 'supportTypeId',
      existsCb: () => task.category.id === CategoryTypeIds.support,
      label: {
        content: 'Тип обращения',
        required: false,
      },
      select: {
        size: 'lg',
        options: supportTypes,
      },
      validators: {
        dataType: 'NUMBER',
        required: true,
      },
    },
    {
      id: 'productId',
      replaceCb: (elem, formData) => {
        const newElem = Object.assign({}, elem)
        // eslint-disable-next-line
        newElem.loadOptions!.entity!.optionListId =
          formData.supportTypeId === telegramSupportTypeId ? 'only_telegram' : 'only_connected'
        return newElem
      },
      label: {
        content: 'Продукт',
        required: false,
      },
      select: {
        size: 'lg',
        addLabelIntoValue: 'productName',
      },
      validators: {
        dataType: 'NUMBER',
        required: true,
      },
      depends: ['clientId', { field: 'supportTypeId', useInFilter: false }],
      loadOptions: {
        entity: {
          entityId: 'product',
          optionListId: 'only_connected',
        },
      },
    },
    buildSubmitCancelButtons(onClose),
  ] as ILegoElem[]
}

export const fetchTaskProperties = async () => {
  return NG_API_CLIENT.get('property', {
    selector: {
      fields: ['id', 'name', 'gid'],
      predicates: [
        {
          field: 'gid',
          operator: 'IN',
          values: [SUPPORT_TYPE_PROPERTY_GID, PRODUCT_PROPERTY_GID],
        },
      ],
    },
  })
}

interface IClient {
  id: number
  name: string
  kind: string
}

interface IClientRelationLink {
  clientId: number
  clientName: string
  managerClientId: number
}

export const fetchClientList = async () => {
  return NG_API_CLIENT.get<IClient>('client', {
    selector: {
      fields: ['id', 'name', 'kind'],
      predicates: [
        {
          field: 'kind',
          operator: 'IN',
          values: ['agency', 'advertiser'],
        },
      ],
    },
  })
}

export const fetchClientRelationLinkList = async () => {
  return NG_API_CLIENT.get<IClientRelationLink>('clientRelationLink' as any, {
    selector: {
      fields: ['managerClientId', 'clientId', 'clientName'],
      ordering: [
        {
          field: 'clientName',
          sortOrder: 'ASC',
        },
      ],
    },
  })
}

/**
 * Переделываем списки под старый формат, чтобы не влезать в глобальное переписывание всего.
 * Этот же формат ждет функция `deepClientSearch`.
 * И все это будет не нужно как только сделаем нормальный выбор в древовидном инпуте
 */
export const buildOldStyleClientList = (clientRelationLinkList: IClientRelationLink[], clientList: IClient[]) => {
  const subClientsByManagerClientId: { [key: number]: { id: number; name: string }[] } = {}
  for (const item of clientRelationLinkList) {
    const managerClientId = item.managerClientId
    subClientsByManagerClientId[managerClientId] = subClientsByManagerClientId[managerClientId] || []
    subClientsByManagerClientId[managerClientId].push({
      id: item.clientId,
      name: item.clientName,
    })
  }

  return clientList
    .map(item => {
      const subClients = subClientsByManagerClientId[item.id]
        ? subClientsByManagerClientId[item.id].sort((a, b) => (a.name > b.name ? 1 : -1))
        : undefined
      return {
        id: item.id,
        name: item.name,
        kind: item.kind,
        clients: subClients,
      }
    })
    .sort((a, b) => (a.name > b.name ? 1 : -1))
}

interface IFetchClientAccountList {
  accountId: number
  accountFirstName: string
  accountLastName: string
}

export const fetchClientAccountList = async (clientId: number) => {
  return NG_API_CLIENT.get<IFetchClientAccountList>('clientAccount' as any, {
    selector: {
      fields: ['accountId', 'accountFirstName', 'accountLastName'],
      predicates: [
        {
          field: 'clientId',
          operator: 'EQUALS',
          values: [clientId],
        },
        {
          field: 'accountActive',
          operator: 'EQUALS',
          values: [true],
        },
        {
          field: 'accountRole',
          operator: 'IN',
          values: [Roles.AGENCY_MANAGER, Roles.AGENCY_PRIME_MANAGER, Roles.CLIENT_MANAGER, Roles.CLIENT_PRIME_MANAGER],
        },
      ],
      ordering: [
        {
          field: 'accountName',
          sortOrder: 'ASC',
        },
      ],
    },
  })
}

interface IFetchReportCompanyForOldId {
  id: number
}

export const fetchReportCompanyForOldId = async (newId: number) => {
  return NG_API_CLIENT.get<IFetchReportCompanyForOldId>('reportCompany' as any, {
    selector: {
      fields: ['id'],
      predicates: [
        {
          field: 'newId',
          operator: 'EQUALS',
          values: [newId],
        },
      ],
    },
  })
}

export const fetchTaskPropertyOptionsList = async (propertyId: number) => {
  return NG_API_CLIENT.get('propertyOption', {
    selector: {
      fields: ['id', 'name', 'description', 'gid'],
      predicates: [
        {
          field: 'propertyId',
          operator: 'EQUALS',
          values: [propertyId],
        },
      ],
      ordering: [
        {
          field: 'id',
          sortOrder: 'ASC',
        },
      ],
    },
  })
}

export const fetchProductList = async () => {
  return NG_API_CLIENT.get('product', {
    selector: {
      fields: ['id', 'name', 'status', 'logo'],
    },
  })
}

export const changeTaskPropertyValues = async (data: ISubmitTaskPropertyValues) => {
  return NG_API_CLIENT.mutate('propertyValue', {
    operations: [
      {
        operator: 'SET',
        operand: {
          id: data?.supportType?.id,
          propertyOptionValue: [data?.supportType?.value],
        },
      },
      {
        operator: 'SET',
        operand: {
          id: data.product.id,
          integerValue: data.product.value,
        },
      },
    ],
  })
}
